import type { WebhookEndpointHealth } from '@compass/client/types/webhook';
import { webhookService } from '@services/api';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryKeys } from '@/services/query.config';

import { chunkIds, healthByEndpointId, MAX_HEALTH_ENDPOINT_IDS } from '../utils';

/**
 * Endpoint health (24-hour success rate, p95, last success, backlog) from the
 * delivery dispatcher.
 *
 * Deliberately separate from the endpoint list and failure-tolerant: when the
 * dispatcher is down this query fails on its own, the health cells render "—",
 * and endpoint management keeps working. It never throws into an error boundary.
 */
export function useWebhookEndpointHealth(
  organizationId: string,
  endpointIds: string[],
  enabled: boolean,
) {
  const ids = useMemo(() => [...endpointIds].sort(), [endpointIds]);
  const query = useQuery({
    queryKey: queryKeys.webhooks.health(organizationId, ids),
    queryFn: async (): Promise<WebhookEndpointHealth[]> => {
      const pages = await Promise.all(
        chunkIds(ids, MAX_HEALTH_ENDPOINT_IDS).map(chunk =>
          webhookService.getWebhookEndpointHealth({
            organization_id: organizationId,
            endpoint_ids: chunk,
          }),
        ),
      );
      return pages.flatMap(page => page?.endpoints ?? []);
    },
    enabled: enabled && !!organizationId && ids.length > 0,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    throwOnError: false,
  });

  const byId = useMemo(() => healthByEndpointId(query.data), [query.data]);
  return { byId, isLoading: query.isLoading, isError: query.isError };
}
